/**
 * One row of the Damage / Item Status checklist: label, Yes/No pair, and a
 * note. The pair is the skill's radio-option treatment (§12) at a 44px touch
 * height; rows are divided by hairlines, not cards. Styling: `.ci-yn*` in
 * checkInDevice.css.
 *
 * The note sits behind "Add note" rather than being open on every row: on one
 * page with every section, always-open notes nearly doubled its length for a
 * clean device. A row that already has a note shows it open.
 */

import React, { useState } from 'react';
import { TextField } from '@mui/material';
import type { CheckInInspectionCheck, CheckInYesNo } from '@techgitter/contracts';

export interface YesNoCheckRowProps {
  label: string;
  value: CheckInInspectionCheck;
  onChange: (next: CheckInInspectionCheck) => void;
  readOnly?: boolean;
}

const OPTIONS: { value: CheckInYesNo; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const YesNoCheckRow: React.FC<YesNoCheckRowProps> = ({ label, value, onChange, readOnly = false }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const showNote = noteOpen || !!value.notes;

  // Tapping the selected option again clears it.
  const pick = (picked: CheckInYesNo) =>
    onChange({ status: value.status === picked ? null : picked, notes: value.notes });

  return (
    <div className="ci-yn-row">
      <div className="ci-yn-row__line">
        <span className="ci-yn-row__label">{label}</span>
        {!readOnly && !showNote && (
          <button type="button" className="ci-yn-row__addnote" onClick={() => setNoteOpen(true)}>
            Add note
          </button>
        )}
        <div className="ci-yn" role="radiogroup" aria-label={label}>
          {OPTIONS.map(opt => {
            const selected = value.status === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={readOnly}
                className={`ci-yn__opt${selected ? ' ci-yn__opt--selected' : ''}`}
                onClick={() => pick(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {showNote &&
        (readOnly ? (
          <div className="ci-check__notes">{value.notes}</div>
        ) : (
          <TextField
            size="small"
            fullWidth
            autoFocus={noteOpen && !value.notes}
            placeholder={`Note — ${label.toLowerCase()}`}
            value={value.notes ?? ''}
            onChange={e => onChange({ status: value.status, notes: e.target.value || null })}
          />
        ))}
    </div>
  );
};
